import React, { useEffect, useMemo, useState } from "react";
import type { ArtworkStatusAdminDto, CreateArtworkStatusDto, UpdateArtworkStatusDto, } from "../models/ArtworkStatus";
import { useApiClient } from '../components/useApiClient';
import useAuthToken from '../functions/useAuthToken';
import { Select, MenuItem, Tooltip, } from '@mui/material';

const CATEGORIES = [
    { value: 1, label: "Commercial" },
    { value: 2, label: "Loan" },
    { value: 3, label: "Location" },
    { value: 4, label: "Admin" },
    { value: 5, label: "Other" },
];

const EXT_TYPES = ["NONE", "VALUE", "OUT", "FROM", "AUCT"] as const;

// UI styling hint mapping
const CODE_HINTS: Record<string, string> = {
    AVAIL: "status-badge-available",
    SOLD: "status-badge-sold",
    HOLD: "status-badge-reserved",
    NFS: "status-badge-not-for-sale",
    REST: "status-badge-restoration",
    LOAN_OUT: "status-badge-on-loan",
};

const EX_INFO_HINTS: Record<string, string> = {
    NONE: "",
    VALUE: "Valuation details",
    AUCT: "Auction lot info",
    OUT: "Destination 'out to' details",
    FROM: "Source 'in from' details",
};

export default function AdminArtworkStatusesPage() {

    //API
    const getAccessToken = useAuthToken();
    const { get, put, putJson, post, postJson, delete: del } = useApiClient();

    const [items, setItems] = useState<ArtworkStatusAdminDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Add form
    const [newName, setNewName] = useState("");
    const [newCode, setNewCode] = useState("");
    const [newCategory, setNewCategory] = useState<number>(5);
    const [newSortOrder, setNewSortOrder] = useState<number>(0);
    const [newExt, setNewExt] = useState<string>("NONE");

    // Edit modal
    const [editTarget, setEditTarget] = useState<ArtworkStatusAdminDto | null>(null);
    const [editName, setEditName] = useState("");
    const [editCode, setEditCode] = useState("");
    const [editCategory, setEditCategory] = useState<number>(1);
    const [editSortOrder, setEditSortOrder] = useState<number>(0);
    const [editExt, setEditExt] = useState<string>("NONE");
    const [saving, setSaving] = useState(false);

    // Delete modal
    const [deleteTarget, setDeleteTarget] = useState<ArtworkStatusAdminDto | null>(null);
    const [deleting, setDeleting] = useState(false);

    const sorted = useMemo(() => {
        return [...items].sort((a, b) => {
            // 1️. Category
            if (a.category !== b.category) {
                return a.category - b.category;
            }

            // 2️. SortOrder
            if (a.sortOrder !== b.sortOrder) {
                return a.sortOrder - b.sortOrder;
            }

            // 3️. Name (case-insensitive, locale aware)
            return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
        });
    }, [items]);

    async function adminArtworkStatusesApi(
        method: 'list' | 'create' | 'update' | 'remove',
        id?: number,
        dto?: any
    ): Promise<ArtworkStatusAdminDto[] | ArtworkStatusAdminDto | null> {
        const accessToken = await getAccessToken();
        if(!accessToken) throw new Error('Invalid access token');

        switch (method) {
            case 'list':
                return await get<ArtworkStatusAdminDto[]>("admin/artwork-statuses", accessToken);
            case 'create':
                return await postJson<ArtworkStatusAdminDto>("admin/artwork-statuses", accessToken, JSON.stringify(dto));
            case 'update':
                return await putJson<ArtworkStatusAdminDto>(`admin/artwork-statuses/${id}`, accessToken, JSON.stringify(dto));
            case 'remove':
                return await del(`admin/artwork-statuses/${id}`, accessToken);
            default: return null;
        }
    };

    async function load() {
        setError(null);
        setLoading(true);
        try {
            const data = await adminArtworkStatusesApi("list");
            if (data && Array.isArray(data)) {
                setItems(data);
            }
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? "Failed to load statuses.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { void load(); }, []);

    async function addStatus() {
        const name = newName.trim();
        if (!name) { setError("Name is required."); return; }

        setError(null);
        try {
            const dto: CreateArtworkStatusDto = {
                name: name,
                code: newCode.trim(),
                category: newCategory,
                sortOrder: Number(newSortOrder) || 0,
                extendedInfoType: newExt,
            }

            const created = await adminArtworkStatusesApi("create", undefined, dto);

            if (created && !Array.isArray(created)) {
                setItems(prev => [...prev, created]);
            }

            setNewName(""); setNewCode(""); setNewCategory(1); setNewSortOrder(0); setNewExt("NONE");
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? "Failed to create status.");
        }
    }

    function openEdit(item: ArtworkStatusAdminDto) {
        setEditTarget(item);
        setEditName(item.name);
        setEditCode(item.code ?? "");
        setEditCategory(item.category);
        setEditSortOrder(item.sortOrder);
        setEditExt(item.extendedInfoType ?? "NONE");
        setError(null);
    }

    function closeEdit() {
        setEditTarget(null);
        setSaving(false);
    }

    async function saveEdit() {
        if (!editTarget) return;
        const name = editName.trim();
        if (!name) { setError("Name is required."); return; }

        setSaving(true);
        setError(null);

        try {
            const dto: UpdateArtworkStatusDto = {
                name: name,
                code: editCode.trim(),
                category: editCategory,
                sortOrder: Number(editSortOrder) || 0,
                extendedInfoType: editExt,
            }

            const updated = await adminArtworkStatusesApi("update", editTarget.artworkStatusId, dto);

            if (updated && !Array.isArray(updated)) {
                setItems(prev => prev.map(x => x.artworkStatusId === updated.artworkStatusId ? updated : x));
            }

            closeEdit();
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? "Failed to update status.");
        } finally {
            setSaving(false);
        }
    }

    async function confirmDelete() {
        if (!deleteTarget) return;
        setDeleting(true);
        setError(null);

        try {
            await adminArtworkStatusesApi("remove", deleteTarget.artworkStatusId);
            setItems(prev => prev.filter(x => x.artworkStatusId !== deleteTarget.artworkStatusId));
            setDeleteTarget(null);
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? "Failed to delete status.");
        } finally {
            setDeleting(false);
        }
    }

    return (
        <div style={{ padding: 24, maxWidth: 1100 }}>
            <h2>Admin · Artwork Statuses</h2>
            <p>System statuses can only change Name and Category. Custom statuses can be fully edited and deleted.</p>

            {error && (
                <div style={{ marginTop: 12, padding: 12, border: "1px solid #ddd" }}>
                    {error}
                </div>
            )}

            {/* Add */}
            <div style={{ marginTop: 16, border: "1px solid #eee", padding: 12 }}>
                <h3 style={{ marginTop: 0 }}>Add Custom Status</h3>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 8 }}>
                    <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name" required/>
                    <input value={newCode} onChange={e => setNewCode(e.target.value)} placeholder="Code (optional)" hidden />

                    <Tooltip title="Status category for grouping" placement="top-end" >
                    <Select value={newCategory} onChange={e => setNewCategory(Number(e.target.value))}>
                            {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                        </Select>
                    </Tooltip>
                    <input
                        type="number"
                        value={newSortOrder}
                        onChange={e => setNewSortOrder(Number(e.target.value))}
                        placeholder="Sort"
                        hidden
                    />
                    <Tooltip title="Type of extended information to be added for the status or 'none'" placement="top" >
                    <Select value={newExt} onChange={e => setNewExt(e.target.value)}>
                            {EXT_TYPES.map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                        </Select>
                    </Tooltip>
                </div>
                <div style={{ marginTop: 8 }}>
                    <button onClick={() => void addStatus()} disabled={!newName.trim()}>Add</button>
                    <button onClick={() => void load()} style={{ marginLeft: 8 }}>Refresh</button>
                </div>
            </div>

            {/* List */}
            <div style={{ marginTop: 16 }}>
                {loading ? (
                    <div>Loading…</div>
                ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={th}>Category</th>
                                <th style={th}>Name</th>
                                <th style={th}>Sort</th>
                                <th style={th}>Extended Info</th>
                                <th style={{ ...th, width: 220 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(s => (
                                <tr key={s.artworkStatusId}>
                                    <td style={td}>{CATEGORIES.find(c => c.value === s.category)?.label ?? s.category}</td>
                                    <td style={td}>{s.name}</td>
                                    <td style={td}>{s.sortOrder}</td>
                                    <td style={td}>{s.extendedInfoType != "NONE" && (s.extendedInfoType ?? "")}
                                        {s.extendedInfoType && EX_INFO_HINTS[s.extendedInfoType] && (
                                            <span style={{ marginLeft: 8, opacity: 0.7 }}>
                                                ({EX_INFO_HINTS[s.extendedInfoType]})
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ ...td, textAlign: "right" }}>
                                        <button onClick={() => openEdit(s)} style={{ marginRight: 8 }}>Edit</button>
                                        <button
                                            onClick={() => setDeleteTarget(s)}
                                            disabled={s.isSystem}
                                            title={s.isSystem ? "System statuses cannot be deleted" : "Delete"}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {sorted.length === 0 && (
                                <tr><td colSpan={7} style={{ padding: 12 }}>No statuses found.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Edit modal */}
            {editTarget && (
                <div style={overlayStyle}>
                    <div style={modalStyle}>
                        <h3>Edit Status</h3>
                        <p style={{ marginTop: 0 }}>
                            {editTarget.isSystem
                                ? "System status: only Name and Category can be changed."
                                : "Custom status: all fields can be changed."}
                        </p>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            <div>
                                <label>Name</label>
                                <input value={editName} onChange={e => setEditName(e.target.value)} style={{ width: "100%" }} />
                            </div>

                            <div>
                                <label>Category</label>
                                <Select value={editCategory} onChange={e => setEditCategory(Number(e.target.value))} style={{ width: "100%" }}>
                                    {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                                </Select>
                            </div>

                            <div>
                                <label>Code</label>
                                <input
                                    value={editCode}
                                    onChange={e => setEditCode(e.target.value)}
                                    style={{ width: "100%" }}
                                    disabled={true}
                                />
                            </div>

                            <div>
                                <label>Sort Order</label>
                                <input
                                    type="number"
                                    value={editSortOrder}
                                    onChange={e => setEditSortOrder(Number(e.target.value))}
                                    style={{ width: "100%" }}
                                    disabled={editTarget.isSystem}
                                />
                            </div>

                            <div>
                                <label>Extended Info</label>
                                <Select
                                    value={editExt}
                                    onChange={e => setEditExt(e.target.value)}
                                    style={{ width: "100%" }}
                                    disabled={editTarget.isSystem}
                                >
                                    {EXT_TYPES.map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                                </Select>
                            </div>
                        </div>

                        <div style={{ marginTop: 16, textAlign: "right" }}>
                            <button onClick={closeEdit} disabled={saving} style={{ marginRight: 8 }}>Cancel</button>
                            <button onClick={() => void saveEdit()} disabled={saving}>
                                {saving ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete modal */}
            {deleteTarget && (
                <div style={overlayStyle}>
                    <div style={modalStyle}>
                        <h3>Delete Status</h3>
                        <p>
                            Delete "<strong>{deleteTarget.name}</strong>"?
                        </p>
                        <div style={{ textAlign: "right" }}>
                            <button onClick={() => setDeleteTarget(null)} disabled={deleting} style={{ marginRight: 8 }}>
                                Cancel
                            </button>
                            <button
                                onClick={() => void confirmDelete()}
                                disabled={deleting || deleteTarget.isSystem}
                                title={deleteTarget.isSystem ? "System statuses cannot be deleted" : "Delete"}
                            >
                                {deleting ? "Deleting…" : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const th: React.CSSProperties = { textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 };
const td: React.CSSProperties = { borderBottom: "1px solid #eee", padding: 8 };

const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
    background: "white",
    padding: 24,
    borderRadius: 8,
    width: 640,
    maxWidth: "95%",
};
